import { Button } from '../../interaction/Buttons/Default Button/Button';
import { ButtonProps, Divider, Icon, Menu, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/user-monitoring';

interface VerticallyAlignCenterDivProps {
  $verticallyAlignCenter: boolean;
}
const VerticallyAlignCenterDiv = styled.div<VerticallyAlignCenterDivProps>`
  margin: ${$verticallyAlignCenter =>
    $verticallyAlignCenter ? 'margin: calc(50% - 5px) 8px 0 0' : ''};
`;

type HorizontalAlignment = 'left' | 'right';

export interface SelectAction {
  /**
   * What should the action button say?
   */
  display: string;
  /**
   * Is this menu item available?
   */
  disabled?: boolean;
  /**
   * Optional click handler
   */
  callback?: () => void;
  /**
   * Optional name for user monitoring in datadog
   */
  ddTrackingName?: string;
  defaultSelected?: boolean;
}

export interface SelectActionMenuProps {
  /**
   * What are the action items?
   */
  actions: SelectAction[];
  /**
   * Secondary menu action items appear after a divider
   */
  secondaryActions?: SelectAction[];
  /**
   * How should the pop up menu align to the three dots?
   */
  horizontalAlign?: HorizontalAlignment;
  /**
   * Should we center the action items?
   */
  verticallyAlignCenter?: boolean;
  color?: ButtonProps['color']; // Use a more useful type
  borderColor?: string;
  menuIcon?: string;
}

/**
 * Action select menu component for displaying a list of app functions
 * ```import { ActionSelectMenu } from '@act/storybook';```
 */
export const ActionSelectMenu = ({
  actions,
  secondaryActions,
  horizontalAlign = 'left',
  verticallyAlignCenter = false,
  menuIcon,
}: SelectActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentAction, setCurrentAction] = useState<SelectAction>();

  useEffect(() => {
    let hasDefault = false;
    for (const action of actions) {
      if (action.defaultSelected) {
        setCurrentAction(action);
        hasDefault = true;
        break;
      }
    }
    if (!hasDefault && actions.length) {
      setCurrentAction(actions[0]);
    }
  }, [actions]);

  const open = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = (action: SelectAction) => {
    setCurrentAction(action);
    handleClose();
    if (action.callback) action.callback();
  };
  const existsEnabledAction = !!actions.find(a => !a.disabled);
  const existsEnabledSecondaryAction =
    secondaryActions && !!secondaryActions.find(sa => !sa.disabled);

  return (
    <VerticallyAlignCenterDiv $verticallyAlignCenter={verticallyAlignCenter}>
      {(existsEnabledAction || existsEnabledSecondaryAction) && (
        <Button
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpenMenu}
        >
          {menuIcon && <Icon className="icon">{menuIcon}</Icon>}
          {currentAction?.display || 'Actions'}
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={
          horizontalAlign === 'right'
            ? { vertical: 'top', horizontal: 'right' }
            : { vertical: 'top', horizontal: 'left' }
        }
        anchorOrigin={
          horizontalAlign === 'right'
            ? { vertical: 'bottom', horizontal: 'right' }
            : { vertical: 'bottom', horizontal: 'left' }
        }
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {actions.map(a => (
          <MenuItem
            className="action-item"
            onClick={() => handleClick(a)}
            key={a.display}
            disabled={a.disabled || false}
            {...generateDatadogTrackingProps(a.ddTrackingName)}
          >
            {a.display}
          </MenuItem>
        ))}
        {!!secondaryActions?.length && <Divider />}
        {!!secondaryActions?.length &&
          secondaryActions.map(a => (
            <MenuItem
              onClick={() => handleClick(a)}
              key={a.display}
              disabled={a.disabled || false}
            >
              {a.display}
            </MenuItem>
          ))}
      </Menu>
    </VerticallyAlignCenterDiv>
  );
};
